import type { Generator, GridState, Load, SimConstants } from "./types.ts";

/** Step a single generator forward by dt game-minutes. */
export function stepGenerator(g: Generator, dtMin: number, gameMinutes: number): void {
  // Update availability for renewables / dynamic units
  if (g.availabilityCurve) g.availability = clamp(g.availabilityCurve(gameMinutes), 0, 1);

  switch (g.status) {
    case "off":
      g.output = 0;
      // Don't force setpoint = 0 for batteries; player intent is preserved
      if (g.type !== "battery") g.setpoint = 0;
      return;
    case "starting":
      g.statusTimer -= dtMin;
      if (g.statusTimer <= 0) {
        g.status = "on";
        g.statusTimer = 0;
        g.output = g.type === "battery" ? 0 : g.pMin;
        if (g.type !== "battery" && g.setpoint < g.pMin) g.setpoint = g.pMin;
      }
      return;
    case "stopping":
      g.statusTimer -= dtMin;
      g.output = signedRamp(g.output, 0, g.rampRate * dtMin);
      if (g.statusTimer <= 0 || Math.abs(g.output) < 0.5) {
        g.status = "off";
        g.statusTimer = 0;
        g.output = 0;
        if (g.type !== "battery") g.setpoint = 0;
      }
      return;
    case "tripped":
      g.output = 0;
      if (g.type !== "battery") g.setpoint = 0;
      return;
    case "on": {
      if (g.type === "battery") {
        // Battery: setpoint can be -pMax (charge) to +pMax (discharge)
        // Constrained by SoC: empty → can't discharge, full → can't charge
        const soc = g.stateOfCharge ?? 0;
        const cap = g.storageCapacity ?? 0;
        let target = clamp(g.setpoint, -g.pMax, g.pMax);
        if (soc <= 0.01) target = Math.min(target, 0); // can only charge if empty
        if (soc >= cap - 0.01) target = Math.max(target, 0); // can only discharge if full
        const maxDelta = g.rampRate * dtMin;
        g.output = signedRamp(g.output, target, maxDelta);
        // SoC update: discharging (output>0) drains; charging (output<0) fills with efficiency
        const eta = g.chargeEfficiency ?? 0.92;
        const dtHours = dtMin / 60;
        const dSoc = g.output > 0
          ? -g.output * dtHours / eta // efficiency penalty on discharge
          : -g.output * dtHours * eta; // and on charge
        g.stateOfCharge = clamp(soc + dSoc, 0, cap);
      } else {
        // Renewables: max output limited by current availability
        const ceiling = g.pMax * g.availability;
        const floor = g.type === "wind" || g.type === "solar" ? 0 : g.pMin;
        const target = clamp(g.setpoint, floor, ceiling);
        const maxDelta = g.rampRate * dtMin;
        g.output = clamp(signedRamp(g.output, target, maxDelta), floor, ceiling);
        // Renewables get whatever is available, regardless of setpoint
        if (g.type === "wind" || g.type === "solar") {
          // Auto-track availability (free fuel, take all you can)
          g.output = ceiling;
        }
      }
      return;
    }
  }
}

/** Aggregate demand from all loads at the current time. */
export function totalDemand(loads: Load[], gameMinutes: number): number {
  let d = 0;
  for (const l of loads) {
    if (l.shed) continue;
    const mult = l.curve ? l.curve(gameMinutes) : 1;
    d += l.baseDemand * mult;
  }
  return d;
}

/** Per-load demand at this moment (for bus injections). */
export function demandFor(loads: Load[], gameMinutes: number, id: string): number {
  const l = loads.find((x) => x.id === id);
  if (!l || l.shed) return 0;
  const mult = l.curve ? l.curve(gameMinutes) : 1;
  return l.baseDemand * mult;
}

/** Sum generator outputs. */
export function totalSupply(generators: Generator[]): number {
  let s = 0;
  for (const g of generators) s += g.output;
  return s;
}

/**
 * Step the system frequency given supply/demand imbalance and dt in game-minutes.
 * Simplified swing equation with damping. 1 game-minute ≡ 1 physics-second.
 */
export function stepFrequency(state: GridState, c: SimConstants, dtMin: number): void {
  const f0 = c.nominalFrequency;
  const imbalance = (state.supplyMW - state.demandMW) / c.systemBase;
  const dfdt = (f0 / (2 * c.inertiaConstant)) * imbalance - c.damping * (state.frequency - f0);
  state.frequency += dfdt * dtMin;
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/** Move x toward target by at most delta (works for negative→positive transitions). */
function signedRamp(x: number, target: number, delta: number): number {
  const diff = target - x;
  if (Math.abs(diff) <= delta) return target;
  return x + Math.sign(diff) * delta;
}
